/**
 * DailyAssignmentModal — modal bloccante mattutino per RM (o FO Manager con delega).
 *
 * v95: supporta range di date per ferie/malattia/riposo (max 3 mesi).
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Loader2, Building2, Bed, Palmtree, Thermometer, CheckCircle2,
  CalendarDays, AlertTriangle,
} from "lucide-react";

const ABSENCE_TIPI = [
  { key: "riposo", label: "In riposo", Icon: Bed },
  { key: "ferie", label: "In ferie", Icon: Palmtree },
  { key: "malattia", label: "In malattia", Icon: Thermometer },
];

const MESI = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];

const fmtDay = (d) => `${d.getDate()} ${MESI[d.getMonth()]} ${d.getFullYear()}`;
const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const toIso = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export default function DailyAssignmentModal() {
  const { user, isLoading: authLoading } = useAuth();
  const [status, setStatus] = useState(null);
  const [tipo, setTipo] = useState(null);        // "operativo" | "riposo" | "ferie" | "malattia"
  const [hotelId, setHotelId] = useState("");
  const [nota, setNota] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [checked, setChecked] = useState(false);
  // Range state (per assenze)
  const [range, setRange] = useState({ from: startOfToday(), to: startOfToday() });
  const [preset, setPreset] = useState("today"); // today | tomorrow | week | custom
  const [popoverOpen, setPopoverOpen] = useState(false);

  useEffect(() => {
    if (authLoading || !user?.id) return;
    if (checked) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/me/daily-assignment");
        if (!alive) return;
        setStatus(data);
        if (data?.needs_choice) {
          if (data.assignable_hotels?.length === 1) setHotelId(data.assignable_hotels[0].id);
        }
      } catch (_) {
        // silenzia
      } finally {
        if (alive) setChecked(true);
      }
    })();
    return () => { alive = false; };
  }, [user?.id, authLoading, checked]);

  const needs = !!status?.needs_choice;
  const isAbsence = tipo === "riposo" || tipo === "ferie" || tipo === "malattia";

  const rangeDays = useMemo(() => {
    if (!range?.from || !range?.to) return 0;
    return Math.round((range.to - range.from) / 86400000) + 1;
  }, [range]);
  const rangeInvalid = rangeDays <= 0 || rangeDays > 90;

  const applyPreset = (k) => {
    setPreset(k);
    const today = startOfToday();
    if (k === "today") setRange({ from: today, to: today });
    else if (k === "tomorrow") { const t = addDays(today, 1); setRange({ from: t, to: t }); }
    else if (k === "week") setRange({ from: today, to: addDays(today, 6) });
    // v102 — Preset retroattivi (SA può andare oltre 30gg; manager fino a 30gg)
    else if (k === "last_week") {
      const dow = today.getDay() || 7; // 1=lun ... 7=dom
      const lastMonday = addDays(today, -(dow - 1) - 7);
      const lastSunday = addDays(lastMonday, 6);
      setRange({ from: lastMonday, to: lastSunday });
    }
    else if (k === "last_month") {
      const firstOfThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastOfPrevMonth = addDays(firstOfThisMonth, -1);
      const firstOfPrevMonth = new Date(lastOfPrevMonth.getFullYear(), lastOfPrevMonth.getMonth(), 1);
      setRange({ from: firstOfPrevMonth, to: lastOfPrevMonth });
    }
  };

  const rangeLabel = useMemo(() => {
    if (!range?.from) return "Seleziona intervallo";
    if (!range.to || range.from.getTime() === range.to.getTime()) return `Solo ${fmtDay(range.from)}`;
    return `${fmtDay(range.from)} → ${fmtDay(range.to)}`;
  }, [range]);

  const submit = async () => {
    if (!tipo) return;
    if (tipo === "operativo" && (status?.assignable_hotels?.length || 0) > 1 && !hotelId) {
      toast.error("Seleziona l'hotel su cui sei operativo oggi");
      return;
    }
    if (isAbsence && rangeInvalid) {
      toast.error(rangeDays > 90 ? "Range massimo 90 giorni" : "Intervallo non valido");
      return;
    }
    setSubmitting(true);
    try {
      if (isAbsence && rangeDays > 1) {
        // Range API
        const payload = {
          tipo,
          date_from: toIso(range.from),
          date_to: toIso(range.to),
          nota: nota.trim(),
        };
        const { data } = await api.post("/me/daily-assignment/range", payload);
        toast.success(
          `Assenza registrata dal ${fmtDay(range.from)} al ${fmtDay(range.to)} — ${data.created} giorni · ${data.reassigned} task riassegnate`
        );
        setStatus((s) => ({ ...s, needs_choice: false }));
      } else {
        // Legacy single-day
        const payload = { tipo, nota: nota.trim() };
        if (tipo === "operativo") payload.hotel_id = hotelId || status.assignable_hotels[0]?.id;
        const { data } = await api.post("/me/daily-assignment", payload);
        toast.success(
          tipo === "operativo"
            ? `Buona giornata! Sei operativo su ${status.assignable_hotels.find((h) => h.id === payload.hotel_id)?.nome}`
            : `Buon riposo — assenza (${tipo}) registrata`
        );
        setStatus((s) => ({ ...s, needs_choice: false, today_assignment: data.assignment }));
      }
      setTipo(null);
      setTimeout(() => window.location.reload(), 900);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  if (!needs) return null;
  const now = new Date();
  const dataStr = now.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });

  return (
    <Dialog open={true}>
      <DialogContent
        className="sm:max-w-lg [&>button.absolute]:hidden max-h-[92vh] overflow-y-auto"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        data-testid="daily-assignment-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-xl">Buongiorno, {user?.nome || user?.email} 👋</DialogTitle>
          <DialogDescription className="capitalize">{dataStr} — come inizia la tua giornata?</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Card OPERATIVO */}
          <div
            role="button"
            tabIndex={0}
            className={`rounded-xl border-2 p-4 cursor-pointer transition-colors ${tipo === "operativo" ? "border-lavender-500 bg-lavender-50/50 dark:bg-lavender-950/20" : "border-border/60 hover:border-lavender-300"}`}
            onClick={() => setTipo("operativo")}
            data-testid="dla-op-card"
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-lavender-100 dark:bg-lavender-950/40 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-lavender-700 dark:text-lavender-300" />
              </div>
              <div className="flex-1">
                <div className="font-medium">Sono operativo oggi</div>
                <div className="text-xs text-muted-foreground">Scegli l&apos;hotel su cui presterai servizio</div>
              </div>
              {tipo === "operativo" && <CheckCircle2 className="h-5 w-5 text-lavender-600" />}
            </div>
            {tipo === "operativo" && (
              <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                {status.assignable_hotels?.length === 1 ? (
                  <div className="text-sm px-3 py-2 rounded-lg bg-background/60 border border-border/60" data-testid="dla-hotel-single">
                    Hotel: <span className="font-medium">{status.assignable_hotels[0].nome}</span>
                  </div>
                ) : (
                  <Select value={hotelId} onValueChange={setHotelId}>
                    <SelectTrigger data-testid="dla-hotel-select"><SelectValue placeholder="Seleziona hotel..." /></SelectTrigger>
                    <SelectContent>
                      {(status.assignable_hotels || []).map((h) => (
                        <SelectItem key={h.id} value={h.id} data-testid={`dla-hotel-opt-${h.id}`}>{h.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}
          </div>

          {/* Cards ASSENZA */}
          <div className="grid grid-cols-3 gap-2">
            {ABSENCE_TIPI.map(({ key, label, Icon }) => (
              <button
                key={key}
                onClick={() => setTipo(key)}
                className={`rounded-xl border-2 p-3 flex flex-col items-center gap-1.5 transition-colors ${tipo === key ? "border-rose-500 bg-rose-50/50 dark:bg-rose-950/20" : "border-border/60 hover:border-rose-300"}`}
                data-testid={`dla-abs-${key}`}
              >
                <Icon className={`h-5 w-5 ${tipo === key ? "text-rose-600" : "text-muted-foreground"}`} />
                <span className="text-sm font-medium">{label}</span>
              </button>
            ))}
          </div>

          {/* Range picker per assenze */}
          {isAbsence && (
            <div className="space-y-2 rounded-xl border border-border/60 bg-background/40 p-3" data-testid="dla-range-section">
              <div className="flex items-center gap-1.5 text-xs uppercase tracking-[0.16em] text-muted-foreground">
                <CalendarDays className="h-3.5 w-3.5" /> Intervallo di assenza
              </div>

              <div className="flex flex-wrap gap-1.5">
                {[
                  { k: "today", l: "Solo oggi" },
                  { k: "tomorrow", l: "Solo domani" },
                  { k: "week", l: "Tutta la settimana" },
                  // v102 — Preset retroattivi
                  { k: "last_week", l: "Settimana scorsa" },
                  { k: "last_month", l: "Mese scorso" },
                  { k: "custom", l: "Custom" },
                ].map((p) => (
                  <button
                    key={p.k}
                    type="button"
                    data-testid={`dla-range-preset-${p.k}`}
                    onClick={() => applyPreset(p.k)}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${preset === p.k
                      ? "bg-rose-500 text-white border-rose-500"
                      : "border-border hover:bg-accent"
                    }`}
                  >{p.l}</button>
                ))}
              </div>

              <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-between text-left font-normal"
                    data-testid="dla-range-trigger"
                    onClick={() => setPreset("custom")}
                  >
                    <span>{rangeLabel}</span>
                    <CalendarDays className="h-4 w-4 opacity-60" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="range"
                    selected={range}
                    onSelect={(r) => { if (r) { setRange(r); setPreset("custom"); } }}
                    numberOfMonths={2}
                    disabled={user?.role === "super_admin" ? { after: addDays(startOfToday(), 365) } : { before: addDays(startOfToday(), -30), after: addDays(startOfToday(), 365) }}
                    data-testid="dla-range-calendar"
                  />
                </PopoverContent>
              </Popover>

              <div className="flex items-center justify-between text-xs">
                <span className={`tabular-nums ${rangeDays > 90 ? "text-rose-600" : "text-muted-foreground"}`}>
                  {rangeDays > 0 ? `${rangeDays} ${rangeDays === 1 ? "giorno" : "giorni"}` : "—"}
                </span>
                {rangeDays > 90 && (
                  <span className="text-rose-600 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Massimo 90 giorni</span>
                )}
              </div>
              {/* v102 — Avviso retroattivo: le task completate dai colleghi non saranno toccate */}
              {range?.from && range.from < startOfToday() && (
                <div className="rounded-lg bg-amber-50/60 dark:bg-amber-950/30 border border-amber-200/60 dark:border-amber-900/40 px-3 py-2 text-[11px] text-amber-800 dark:text-amber-200 flex items-start gap-2" data-testid="dla-backdated-notice">
                  <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                  <span>
                    Range retroattivo: le task <strong>già completate dai colleghi</strong> non verranno toccate. Verranno annullate solo le task ancora <strong>pending</strong> nei giorni passati.
                  </span>
                </div>
              )}
            </div>
          )}

          {isAbsence && (
            <div className="space-y-1.5">
              <Textarea
                placeholder="Nota (opzionale)"
                value={nota}
                onChange={(e) => setNota(e.target.value)}
                className="min-h-[60px]"
                data-testid="dla-nota"
                maxLength={500}
              />
              {tipo === "malattia" && (
                <p className="text-[11px] text-amber-600 dark:text-amber-400" data-testid="dla-malattia-info">
                  Sarà inviata notifica al Capo Area e al Super Admin.
                </p>
              )}
            </div>
          )}

          <Button
            onClick={submit}
            disabled={!tipo || submitting || (isAbsence && rangeInvalid)}
            className="w-full bg-lavender-500 hover:bg-lavender-600 text-white"
            data-testid="dla-submit"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Conferma"}
          </Button>
          <p className="text-[10px] text-muted-foreground text-center">
            {isAbsence && rangeDays > 1
              ? "L'assenza copre l'intero intervallo selezionato. I DLA esistenti nel range verranno sovrascritti."
              : "La scelta è definitiva per la giornata di oggi."}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
